import {
  lazy as reactLazy,
  type ComponentType,
  type LazyExoticComponent,
} from 'react'

export type PreloadableComponent<T extends ComponentType<any>> =
  LazyExoticComponent<T> & { preload: () => Promise<unknown> }

/**
 * Drop-in for `React.lazy` whose returned component also exposes `preload()`,
 * so a route's code-split chunk can be fetched ahead of navigation (e.g. on
 * nav-item hover) instead of only once the user has already clicked — which is
 * what makes a sub-nav view feel like it "takes ages" the first time.
 *
 * The import factory runs at most once; repeat/concurrent `preload()` calls
 * reuse the in-flight promise. A failed prefetch is forgotten so a later
 * attempt (or the real navigation) can retry.
 */
export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  const Component = reactLazy(factory) as PreloadableComponent<T>
  let promise: Promise<unknown> | undefined
  Component.preload = () => {
    if (!promise) {
      promise = factory().catch((err) => {
        promise = undefined
        throw err
      })
    }
    return promise
  }
  return Component
}
